import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import { request, saveTokens, loadTokens } from '@/api/client';
import type { User } from '@/api/types';

export const useAuthStore = defineStore('auth', () => {
  const user = ref<User | null>(null);
  const loading = ref(false);
  const error = ref<string | null>(null);

  const isAuthenticated = computed(() => user.value !== null);
  /**
   * Role gating in the UI hides controls a viewer cannot use. It is a
   * convenience only - the API enforces the same rule on every mutating route,
   * so a viewer who forges a request still gets 403.
   */
  const isOperator = computed(() => user.value?.role === 'operator');

  async function login(email: string, password: string): Promise<boolean> {
    loading.value = true;
    error.value = null;
    try {
      const res = await request<{
        accessToken: string;
        refreshToken: string;
        user: User;
      }>('/auth/login', { method: 'POST', body: { email, password } });
      saveTokens({ accessToken: res.accessToken, refreshToken: res.refreshToken });
      user.value = res.user;
      return true;
    } catch (err) {
      error.value = err instanceof Error ? err.message : 'sign-in failed';
      return false;
    } finally {
      loading.value = false;
    }
  }

  /** Restores the session on a page reload, when tokens are still valid. */
  async function restore(): Promise<boolean> {
    if (!loadTokens()) return false;
    try {
      user.value = await request<User>('/auth/me');
      return true;
    } catch {
      saveTokens(null);
      user.value = null;
      return false;
    }
  }

  async function logout(): Promise<void> {
    const tokens = loadTokens();
    if (tokens?.refreshToken) {
      await request('/auth/logout', {
        method: 'POST',
        body: { refreshToken: tokens.refreshToken },
      }).catch(() => undefined);
    }
    saveTokens(null);
    user.value = null;
  }

  function clearSession(): void {
    saveTokens(null);
    user.value = null;
  }

  return {
    user, loading, error,
    isAuthenticated, isOperator,
    login, restore, logout, clearSession,
  };
});
